import { useT } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import type { CustomerSignal, Pair, Reason, Rule } from "./match"

export const REASON_LABEL = {
  matched: "wb.reason.matched",
  amount_mismatch: "wb.reason.amount_mismatch",
  missing_in_erp: "wb.reason.missing_in_erp",
  missing_in_shopify: "wb.reason.missing_in_shopify",
  duplicate: "wb.reason.duplicate",
} as const

/** One hue per verdict, used by the pills, the sheet's row marks and the chart. */
export const REASON_COLOR: Record<Reason | "review", string> = {
  matched: "var(--success)",
  review: "var(--lineage)",
  amount_mismatch: "var(--warning)",
  duplicate: "color-mix(in oklab, var(--warning) 55%, var(--destructive))",
  missing_in_erp: "var(--destructive)",
  missing_in_shopify: "color-mix(in oklab, var(--destructive) 55%, var(--muted-foreground))",
}

/** `review` marks a matched pair a person must still judge (amount + date, or another customer). */
export function ReasonPill({ reason, rule, review, className }: { reason: Reason; rule: Rule; review?: boolean; className?: string }) {
  const t = useT()
  const fuzzy = reason === "matched" && (review ?? rule === "amount_date")
  const color = fuzzy ? REASON_COLOR.review : REASON_COLOR[reason]
  return (
    <span className={cn("inline-flex h-5 items-center gap-1.5 rounded-full border border-border px-2 text-2xs whitespace-nowrap", className)}>
      <span className="size-1.5 rounded-full" style={{ background: color }} aria-hidden />
      {fuzzy ? t("wb.reason.fuzzy") : t(REASON_LABEL[reason])}
    </span>
  )
}

export function ConfidenceBar({ value }: { value: number }) {
  const t = useT()
  return (
    <span className="inline-flex items-center gap-1.5" title={t("wb.col.confidence")}>
      <span className="h-1 w-10 overflow-hidden rounded-full bg-muted" aria-hidden>
        <span className="block h-full rounded-full bg-foreground/70" style={{ width: `${value}%` }} />
      </span>
      <span className="text-2xs text-muted-foreground tabular-nums">{value}</span>
    </span>
  )
}

/** Whether both sides name the same customer: a signal beside amount and date, never a key. */
export function CustomerMark({ signal, oneSided = false }: { signal: CustomerSignal; oneSided?: boolean }) {
  const t = useT()
  // A pair with only one side has no one to compare.
  if (oneSided) return <span className="text-subtle-foreground">—</span>
  return (
    <span className={cn("text-2xs whitespace-nowrap", signal === "same" ? "text-success" : signal === "different" ? "text-destructive" : "text-subtle-foreground")}>
      {t(`wb.customer.${signal}` as const)}
    </span>
  )
}

/** Each pair's verdict in plain sentences: what the rules saw, and what it means for the books. */
export function useWhy(): (p: Pair) => string {
  const t = useT()
  const verdict = useVerdict()
  return (p) => {
    const base = verdict(p)
    if (!p.shop || !p.erp || p.customer === "unknown") return base
    return `${base} ${p.customer === "same" ? t("wb.why.customerSame") : t("wb.why.customerDifferent", { shop: p.shop.email ?? "", erp: p.erp.customerEmail ?? "" })}`
  }
}

function useVerdict(): (p: Pair) => string {
  const t = useT()
  return (p) => {
    const shop = p.shop?.name ?? ""
    const erp = p.erp?.number ?? ""
    switch (p.reason) {
      case "matched":
        return p.rule === "reference"
          ? t("wb.why.reference", { shop, erp })
          : p.alternatives.length
            ? t("wb.why.ambiguous", { shop, erp, count: p.alternatives.length })
            : p.dayGap
              ? t("wb.why.amountDayApart", { shop, erp })
              : t("wb.why.amountSameDay", { shop, erp })
      case "amount_mismatch":
        return t("wb.why.amountMismatch", { shop, erp, delta: String(Math.abs(p.delta ?? 0)), direction: (p.delta ?? 0) > 0 ? t("wb.why.more") : t("wb.why.less") })
      case "duplicate":
        return t("wb.why.duplicate", { shop, erp })
      case "missing_in_erp":
        return p.rule === "reference" ? t("wb.why.unknownReference", { shop, erp: p.shop?.reference ?? "" }) : t("wb.why.missingInErp", { shop })
      case "missing_in_shopify":
        return t("wb.why.missingInShopify", { erp })
    }
  }
}
